import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { motion } from 'framer-motion';
import { Copy, Check } from 'lucide-react';
import 'highlight.js/styles/github-dark.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = ""
}) => {
  const [copiedBlocks, setCopiedBlocks] = React.useState<Set<number>>(new Set());

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBlocks(prev => new Set(prev).add(index));
      setTimeout(() => {
        setCopiedBlocks(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`prose prose-lg max-w-none
        prose-headings:text-gray-900 prose-headings:font-bold prose-headings:tracking-tight
        prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
        prose-strong:text-gray-900 prose-strong:font-semibold
        prose-em:text-gray-700 prose-em:italic
        prose-code:bg-gray-100 prose-code:text-purple-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-0 prose-pre:rounded-lg prose-pre:overflow-hidden prose-pre:my-8 prose-pre:shadow-lg prose-pre:relative
        prose-a:text-indigo-600 prose-a:no-underline prose-a:font-medium hover:prose-a:underline hover:prose-a:text-indigo-700 prose-a:transition-colors
        prose-blockquote:border-l-indigo-500 prose-blockquote:bg-indigo-50 prose-blockquote:p-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:my-8 prose-blockquote:shadow-sm
        prose-ul:my-6 prose-ul:space-y-3 prose-ul:pl-6
        prose-ol:my-6 prose-ol:space-y-3 prose-ol:pl-6
        prose-li:text-gray-700 prose-li:leading-relaxed prose-li:mb-2
        prose-h1:text-4xl prose-h1:mb-8 prose-h1:mt-0 prose-h1:leading-tight prose-h1:scroll-mt-24
        prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-3 prose-h2:leading-tight prose-h2:scroll-mt-24
        prose-h3:text-2xl prose-h3:mb-4 prose-h3:mt-10 prose-h3:leading-tight prose-h3:scroll-mt-24
        prose-h4:text-xl prose-h4:mb-4 prose-h4:mt-8 prose-h4:leading-snug prose-h4:scroll-mt-24
        prose-h5:text-lg prose-h5:mb-3 prose-h5:mt-6 prose-h5:font-semibold prose-h5:scroll-mt-24
        prose-h6:text-base prose-h6:mb-3 prose-h6:mt-6 prose-h6:font-semibold prose-h6:scroll-mt-24
        prose-hr:border-gray-300 prose-hr:my-12
        prose-table:my-8 prose-table:shadow-sm prose-table:border prose-table:border-gray-200 prose-table:rounded-lg prose-table:overflow-hidden
        prose-thead:border-b prose-thead:border-gray-300 prose-thead:bg-gray-50
        prose-tbody:divide-y prose-tbody:divide-gray-200
        prose-td:py-3 prose-td:px-4 prose-td:text-gray-700
        prose-th:py-3 prose-th:px-4 prose-th:font-semibold prose-th:text-left prose-th:text-gray-900 prose-th:bg-gray-50
        ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          // Custom link component to handle external links
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : '_self'}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-indigo-600 hover:text-indigo-700 transition-colors font-medium"
              {...props}
            >
              {children}
            </a>
          ),
          // Enhanced code block styling with copy functionality
          pre: ({ children, ...props }) => {
            const codeContent = React.Children.toArray(children).join('');
            const index = Math.random(); // Simple index for copy state

            return (
              <div className="relative group my-8">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm leading-relaxed shadow-lg" {...props}>
                  {children}
                </pre>
                <button
                  onClick={() => copyToClipboard(codeContent, index)}
                  className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-md opacity-0 group-hover:opacity-100 transition-all duration-200 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  aria-label="Copy code"
                >
                  {copiedBlocks.has(index) ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            );
          },
          // Enhanced inline code styling
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            if (match) {
              // This is a code block, let the pre component handle it
              return <code className={className} {...props}>{children}</code>;
            }
            // This is inline code
            return (
              <code
                className="bg-gray-100 text-purple-700 px-1.5 py-0.5 rounded text-sm font-mono font-medium"
                {...props}
              >
                {children}
              </code>
            );
          },
          // Enhanced blockquote styling
          blockquote: ({ children, ...props }) => {
            // Filter out conflicting props that framer-motion doesn't accept
            const { onDrag, onDragStart, onDragEnd, ...motionProps } = props as any;
            return (
              <motion.blockquote
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="border-l-4 border-indigo-500 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 pl-8 rounded-r-lg not-italic my-8 text-gray-700 leading-relaxed shadow-sm"
                {...motionProps}
              >
                {children}
              </motion.blockquote>
            );
          },
          // Enhanced paragraph with better spacing
          p: ({ children, ...props }) => (
            <p className="text-gray-700 leading-relaxed mb-6 text-lg" {...props}>
              {children}
            </p>
          ),
          // Enhanced heading components with IDs for TOC
          h1: ({ children, ...props }) => {
            const id = React.Children.toArray(children).join('').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
              <h1 id={id} className="text-4xl font-bold text-gray-900 mb-8 mt-0 leading-tight tracking-tight scroll-mt-24" {...props}>
                {children}
              </h1>
            );
          },
          h2: ({ children, ...props }) => {
            const id = React.Children.toArray(children).join('').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
              <h2 id={id} className="text-3xl font-bold text-gray-900 mb-6 mt-12 pb-3 border-b border-gray-200 leading-tight tracking-tight scroll-mt-24" {...props}>
                {children}
              </h2>
            );
          },
          h3: ({ children, ...props }) => {
            const id = React.Children.toArray(children).join('').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
              <h3 id={id} className="text-2xl font-bold text-gray-900 mb-4 mt-10 leading-tight tracking-tight scroll-mt-24" {...props}>
                {children}
              </h3>
            );
          },
          h4: ({ children, ...props }) => {
            const id = React.Children.toArray(children).join('').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
              <h4 id={id} className="text-xl font-bold text-gray-900 mb-4 mt-8 leading-snug tracking-tight scroll-mt-24" {...props}>
                {children}
              </h4>
            );
          },
          // Enhanced list components
          ul: ({ children, ...props }) => (
            <ul className="my-6 space-y-3 pl-6 list-disc" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="my-6 space-y-3 pl-6 list-decimal" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-gray-700 leading-relaxed mb-2 text-lg" {...props}>
              {children}
            </li>
          ),
          // Enhanced horizontal rule
          hr: ({ ...props }) => (
            <hr className="border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-12" {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </motion.div>
  );
};

export default MarkdownRenderer; 